// DE UM TEXTO PARA UM QUADRINHO.
//
// Item 8 do pedido do operador: "crie uma ferramenta que gere as páginas do quadrinho a partir de
// um único prompt". O backend já fazia isso — `POST /roteirizar` existe e funciona — e nenhuma
// tela chamava a rota. O levantamento registrou como "tem porta, não tem botão", e é a forma mais
// silenciosa de não entregar: o trabalho está feito, passa no teste, e ninguém consegue usar.
//
// ── A ESTRUTURA APARECE ANTES DOS DESENHOS, E ISSO É O DESENHO DA TELA ────────────────────────
//
// Montar o roteiro é instantâneo. Desenhar seis quadros leva minutos. Se a tela esperasse tudo
// para mostrar qualquer coisa, a criança olharia para um carregando por dois minutos sem saber se
// a história dela virou alguma coisa.
//
// Então a página aparece assim que o roteiro volta — com os quadros, os balões e a divisão já
// visíveis — e cada desenho entra no lugar dele conforme fica pronto. Isso muda o que a espera
// significa: em vez de "será que funcionou?", vira "está ficando pronto".
//
// ── AS IMAGENS SÃO GERADAS UMA DE CADA VEZ, DE PROPÓSITO ──────────────────────────────────────
//
// Em série, e não em paralelo. Disparar seis pedidos juntos consumiria seis vezes o limite diário
// de uma vez, e numa sala com trinta alunos derrubaria a cota da escola em minutos. Em série,
// dá para parar no meio — e a criança vê o primeiro quadro pronto enquanto o segundo desenha.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Nav from "../../components/nav";
import { VisualizadorDeDocumento, VisualizadorDePagina } from "../../motores";
import {
  Categoria,
  DocumentoHQ,
  Elemento,
  FormatoPagina,
  PADROES_PAGINA,
  ROTULO_CATEGORIA,
} from "../../api/documento.types";
import Firebase from "../../api/firebase.class";
import { imagensDoDocumento, paraOEditor } from "../../publicar/para-o-editor";
import {
  descartarOTexto,
  descartarRascunho,
  guardarOTexto,
  guardarRascunho,
  recuperarOTexto,
  recuperarRascunho,
} from "./rascunho-guardado";
import { registrarGuardaDeSaida } from "../meus-quadrinhos/saida-sem-salvar";
import { dicasCom, lerOTexto } from "./como-escrever-a-cena";
import { NumerosDoDesenho, PADRAO, perguntarAoServico } from "./os-numeros-que-o-servico-usa";
import { entendeuAHistoria, NAO_ENTENDI } from "./entendeu-a-historia";
import { encolherParaCaber } from "../../api/encolher-imagem";
import { ConteudoRecusadoError, RespostaDeRoteiro, roteirizar } from "../../api/roteiro";
import PlanoDoQuadrinho from "./plano-do-quadrinho";
import { pareceHistoria, Veredicto } from "./parece-historia";
import { falhaParaCrianca, avisosParaCrianca, AvisoParaCrianca, semOsAvisosDoQuadro, semOsAvisosDoCorte } from "./avisos-para-crianca";
import Imgen, { ConteudoRecusadoError as DesenhoRecusadoError, KleinEsgotadoError } from "../../api/imgen";
import { ESTILO_DA_CASA, ESTILOS_DA_TELA, estiloParaEnviar } from "../../api/estilos";
import { descricaoDoQuadro } from "./descricao-do-quadro";
import { reduzirParaReferencia } from "../../api/reduzir-imagem";
import { paginasComoImagens, baixarPDF, baixarPNGs, respirar } from "./baixar-quadrinho";
import { esquecerOsDesenhos, GeracaoGuardada, guardarOsDesenhos, HistoriaGuardada, recuperarAHistoria } from "./desenhos-guardados";
import { useGlobalState } from "../../global-state-context";
import ProgressoDaGeracao, { EstadoDoQuadro, mediaMedida, terminados } from "./progresso";
import PainelDoAssistente from "../../components/painel-do-assistente";
import { LETRAS_DO_NOME } from "../meus-quadrinhos/novo-quadrinho";

import "./roteirizar.scss";

const EXEMPLO =
  "A Bia perdeu o caderno de matemática no recreio.\n" +
  "Ela procura debaixo da mesa e não acha.\n" +
  "— Você viu o meu caderno? — pergunta pro João.\n" +
  "O João aponta pra árvore do pátio: o caderno está lá em cima.";

type EstadoDeArte = "parado" | "montando" | "planejado" | "desenhando" | "pronto";

/** Insere a imagem gerada dentro do quadro certo, atrás dos balões. */
function comImagemNoQuadro(
  documento: DocumentoHQ,
  paginaId: string,
  quadroId: string,
  url: string,
  descricao: string
): DocumentoHQ {
  return {
    ...documento,
    paginas: documento.paginas.map((pagina) => {
      if (pagina.id !== paginaId) return pagina;
      return {
        ...pagina,
        camadas: pagina.camadas.map((camada) => {
          const quadro = camada.elementos.find(
            (e) => e.id === quadroId && e.tipo === "quadro"
          );
          if (!quadro) return camada;

          const imagem: Elemento = {
            id: `img-${quadroId}`,
            tipo: "imagem",
            nome: `Desenho do ${quadro.nome ?? quadroId}`,
            x: quadro.x,
            y: quadro.y,
            largura: quadro.largura,
            altura: quadro.altura,
            refImagem: url,
            descricaoAcessivel: descricao,
          };

          // A ORDEM IMPORTA e é a razão desta função existir em vez de um `push`.
          //
          // Os motores desenham na ordem do array. A imagem entra logo DEPOIS do quadro, e não no
          // fim: no fim, ela cobriria os balões daquele quadro — e uma história em que a fala fica
          // atrás do desenho não é uma história.
          const i = camada.elementos.indexOf(quadro);
          const elementos = [...camada.elementos];
          const jaTem = elementos.findIndex((e) => e.id === imagem.id);
          if (jaTem >= 0) elementos[jaTem] = imagem;
          else elementos.splice(i + 1, 0, imagem);

          return { ...camada, elementos };
        }),
      };
    }),
  };
}

function Roteirizar() {
  const globalState = useGlobalState();

  const [texto, setTexto] = useState("");
  const [titulo, setTitulo] = useState("");
  const [formato, setFormato] = useState<FormatoPagina>("tirinha");
  const [categoria, setCategoria] = useState<Categoria>("livre");

  const [estado, setEstado] = useState<EstadoDeArte>("parado");

  /**
   * VER MAIOR — `T2.85`.
   *
   * A queixa, na letra: *"que visualização péssima, viu? A letra miudíssima."* E ela tinha número,
   * medido em produção em 26/09: a página sai do gerador com **1024 px** e era desenhada a **394 px**
   * — **38,5% do tamanho**. Uma letra de 40 px dentro da imagem aparecia a **15,4 px**.
   *
   * E O AGRAVANTE, que é o que fazia disso defeito e não escolha: **não havia como ver maior.**
   * Medido na mesma passada — imagem não clicável, nenhum botão de ampliar, zoom ou tela cheia,
   * nenhum diálogo. O único botão era "Salvar em Meus quadrinhos".
   *
   * POR QUE A SAÍDA NÃO É SÓ AUMENTAR O PADRÃO: a 860 px cabem duas páginas lado a lado, e ver a
   * história inteira de uma vez é o que a tela de resultado existe para dar. Aumentar por padrão
   * trocaria uma queixa por outra ("agora só vejo uma página"). O que faltava era a **escolha** — e
   * ela é de quem está olhando, não minha.
   *
   * A letra do balão é RASTERIZADA dentro da imagem (ver a prova do `TS-T5.7.1`), então o tamanho
   * dela não está no DOM e não há como aumentar só o texto. O que dá para aumentar é a página, e é
   * isso que a escala faz.
   */
  const [ampliado, setAmpliado] = useState(false);
  const [larguraDaJanela, setLarguraDaJanela] = useState(
    typeof window === "undefined" ? 1024 : window.innerWidth
  );
  useEffect(() => {
    const medir = () => setLarguraDaJanela(window.innerWidth);
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, []);
  // Os 48 px são a calha lateral da página. `max` de 860 porque ampliar nunca pode devolver menos
  // do que o padrão — numa janela estreita "ver maior" ficaria "ver igual", e um botão que não faz
  // nada é pior que botão nenhum (é por isso que ele desaparece, logo abaixo).
  const larguraAmpliada = Math.max(860, Math.min(1600, larguraDaJanela - 48));
  // T12.7 · QUEM É O PERSONAGEM. Campo opcional, e o que ele faz não é dica de tela: a descrição
  // vai para dentro do pedido de desenho de TODO quadro, na frente das aparências que a máquina
  // infere das cenas. É o que faz o personagem atravessar os quadros sem depender do modelo.
  const [personagem, setPersonagem] = useState("");
  // T22 · o estilo da história inteira, por tag (0049 T3.1). O da casa é o padrão.
  const [estilo, setEstilo] = useState(ESTILO_DA_CASA);
  // T17.3 · Os números que o SERVIÇO está usando agora. Começam no padrão e são trocados quando a
  // resposta chega: a dica tem de aparecer na hora, e um número de legenda defasado por um segundo
  // é diferente de uma regra defasada.
  const [numerosDoDesenho, setNumerosDoDesenho] = useState<NumerosDoDesenho>(PADRAO);
  const [documento, setDocumento] = useState<DocumentoHQ | null>(null);
  // T19 · o planejamento que a pessoa vê ANTES de o canvas ser montado e desenhado.
  const [planoPendente, setPlanoPendente] = useState<RespostaDeRoteiro | null>(null);
  const [avisos, setAvisos] = useState<AvisoParaCrianca[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [recusado, setRecusado] = useState(false);
  // ── "ISSO PARECE LETRAS SOLTAS" — `T4.2`, e ela PERGUNTA, nunca recusa ────────────────────
  //
  // UM estado, e nao dois: `letrasSoltas` guarda o veredicto enquanto a pergunta esta na tela, e o
  // "tentar mesmo assim" chama o trabalho DIRETO em vez de lembrar que alguem insistiu. Guardar o
  // consentimento seria calar a guarda para textos que vierem depois.
  const [letrasSoltas, setLetrasSoltas] = useState<Veredicto | null>(null);
  const [progresso, setProgresso] = useState({ feitos: 0, total: 0 });

  /**
   * O ESTADO DE CADA QUADRO, e o instante em que o atual comecou.
   *
   * Os dois existem por causa da leitura do dossie, e nao por capricho: o contador que ja havia
   * respondia QUANTOS, e o T2.86 tem duas perguntas que ele nao responde — "ta gerando de fato?"
   * (precisa de movimento, e movimento precisa de relogio) e "isto que eu estou vendo e o final?"
   * (precisa de estado POR QUADRO).
   */
  const [quadros, setQuadros] = useState<EstadoDoQuadro[]>([]);
  const [comecouEm, setComecouEm] = useState<number | null>(null);
  // T29.8 · a fase antes dos quadros (as fichas dos personagens) e quanto cada quadro levou.
  const [fase, setFase] = useState<string | null>(null);
  const [duracoes, setDuracoes] = useState<number[]>([]);
  // T29.2 · a geração foi interrompida por um recarregar: a tela oferece continuar.
  const [interrompida, setInterrompida] = useState(false);
  // T29.3 · a página aberta em tela cheia (índice), ou nenhuma.
  const [paginaAberta, setPaginaAberta] = useState<number | null>(null);

  // ── T11.3 · REFAZER UM QUADRO SO ────────────────────────────────────────────────────────
  //
  // O relatorio pede *"permitir refazer um quadro so"*, e a tela JA MANDAVA fazer isso: quando um
  // quadro falha, o aviso diz *"tente de novo daqui a pouco, ou desenhe esse quadro no editor"*.
  // Nao havia controle nenhum atras dessa frase — a crianca so podia gerar a historia inteira de
  // novo, e os outros cinco quadros que ficaram bons seriam sorteados outra vez.
  //
  // Para refazer UM quadro preciso de duas coisas que viviam dentro da funcao de gerar: o pedido
  // daquele quadro e a SEMENTE da historia. A semente e o que faz o quadro refeito combinar com
  // os que ja estao na pagina; refazer com semente nova traria outro personagem, que e o defeito
  // do T5.4 de volta num quadro so.
  const [pedidos, setPedidos] = useState<
    { paginaId: string; quadroId: string; prompt: string; cena?: string; quem?: string[] }[]
  >([]);
  const [semente, setSemente] = useState<number | null>(null);
  const [refazendo, setRefazendo] = useState<number | null>(null);
  // T26.R2b · baixar sem conta: a folha montada vira PDF ou PNG.
  const [baixando, setBaixando] = useState<"pdf" | "png" | null>(null);
  const [passoDoBaixar, setPassoDoBaixar] = useState<string | null>(null);
  const paginasNaTela = useRef<HTMLDivElement | null>(null);
  // T26.R10 · enquanto a volta do login lê o banco, nada é gravado nele: medido em 13/10, a tela
  // gravava a história SEM desenhos (a da gaveta) por cima da que tinha, antes de terminar de ler.
  const lendoOBanco = useRef(true);

  const navigate = useNavigate();

  // ── SALVAR NO ACERVO (T2.16) ───────────────────────────────────────────────────────────────
  //
  // Medido em 12/09: esta tela NÃO TINHA caminho de salvar nenhum. A criança escrevia, esperava
  // os quadros serem desenhados um a um, via o quadrinho pronto — e ele existia enquanto a aba
  // estivesse aberta. A frase do `App.tsx` ("salvar precisa de conta, e é aí que o login entra")
  // descrevia uma intenção que não tinha implementação do outro lado.
  const [salvando, setSalvando] = useState(false);
  /**
   * ── ONDE O SALVAR ESTÁ, E POR QUE ISSO É UM ESTADO E NÃO UM SPINNER (`T6.4`) ────────────────
   *
   * Ele, na letra: *"Se eu tentar salvar meus quadrinhos, não sei o que vai acontecer. **Tá
   * salvando infinitamente.** Salvou agora. É, salvou agora."*
   *
   * O salvar CONCLUIU — ele mesmo diz. O que ele reclama é da espera sem retorno, e ela tem
   * tamanho: com três desenhos, são três reencodamentos em `<canvas>` (até seis tentativas cada,
   * pela escada do `T5.6`) mais quatro escritas no Firestore, tudo EM SÉRIE. Dezenas de segundos
   * em que a única coisa que muda na tela é o botão dizer "Salvando…".
   *
   * É a mesma forma de defeito do `T5.3`, e a mesma resposta: o laço sabe exatamente onde está, e
   * o que faltava era ele CONTAR. Dado real, não estimativa — diferente da barra da geração, aqui
   * não há nada para estimar, porque cada passo é uma linha de código que acabou ou não acabou.
   */
  const [passoDoSalvar, setPassoDoSalvar] = useState<string | null>(null);
  const [salvo, setSalvo] = useState<string | null>(null);
  const [avisoDoRascunho, setAvisoDoRascunho] = useState<string | null>(null);

  // T11.2 e T11.4 · O que ha de medivel no que ela escreveu ate agora. Derivado do texto a cada
  // render, e nao estado: estado paralelo ao texto e a forma de os dois divergirem.
  // T16.2 · O SEGUNDO ARGUMENTO É O QUE FAZ A DICA NÃO MENTIR: com o campo do personagem
  // preenchido, o desenho usa menos letras da cena, porque os dois textos dividem o mesmo
  // orçamento no pedido que vai ao desenhador.
  //
  // T17.3 · E O TERCEIRO são os números que o SERVIÇO está usando agora. Desde que as regras
  // saíram do código, esses números podem mudar num painel sem deploy nenhum — e uma tela que
  // continuasse afirmando o número antigo seria o defeito do T16.3 de volta, por outra porta.
  const leitura = lerOTexto(texto, personagem.trim().length > 0, numerosDoDesenho);

  // T17.3 · PERGUNTA AO SERVIÇO quais números estão valendo. Uma vez, ao abrir a tela.
  //
  // Nunca atrasa nada e nunca quebra: a função devolve o padrão em qualquer problema. Uma tela de
  // criança que não abre porque a rota de diagnóstico não respondeu seria um defeito muito maior
  // do que uma dica com o número de ontem.
  useEffect(() => {
    let vivo = true;
    void perguntarAoServico().then((n) => {
      if (vivo) setNumerosDoDesenho(n);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // A VOLTA DO LOGIN. Quem clicou em salvar sem conta foi levada ao login com o quadrinho
  // guardado; aqui ele volta para a tela, em vez de ela encontrar o branco.
  useEffect(() => {
    // ── O TEXTO VOLTA PRIMEIRO, E VOLTA SOZINHO (T9.4) ─────────────────────────────────────
    //
    // Ele é recuperado FORA do `if` do documento, e isso é o conserto do segundo caminho do
    // achado: quem clicou em "Entrar" no alto da página nunca passou pelo botão de salvar, então
    // não há documento guardado — e tinha de haver texto. Medido: 0 de 2 caminhos devolviam o
    // texto, e a tela promete, na primeira linha, que ele volta.
    const digitado = recuperarOTexto();
    if (digitado) {
      setTitulo(digitado.titulo);
      setTexto(digitado.texto);
      // T26.R6 · e as escolhas do formulário, que voltavam ao padrão sem a pessoa perceber.
      if (digitado.formato) setFormato(digitado.formato as FormatoPagina);
      if (digitado.categoria) setCategoria(digitado.categoria as Categoria);
      if (digitado.personagem) setPersonagem(digitado.personagem);
      if (digitado.estilo) setEstilo(digitado.estilo);
    }

    const rascunho = recuperarRascunho();
    // T26.R10 · os desenhos que não cabiam na gaveta da aba voltam do banco do navegador.
    const comDesenhos = recuperarAHistoria().finally(() => {
      lendoOBanco.current = false;
    });
    // T29.2 e T29.4 · e volta com eles o que o «Redesenhar» precisa — e, se a página recarregou
    // no meio dos desenhos, os quadros prontos ficam e os que faltavam podem ser desenhados.
    const devolver = (h: HistoriaGuardada) => {
      setDocumento(h.documento);
      setEstado("pronto");
      const g = h.geracao;
      if (!g) return;
      setPedidos(g.pedidos);
      setSemente(g.semente);
      const voltaram = g.quadros.map((q) => (q === "esperando" || q === "desenhando" ? "falhou" : q));
      setQuadros(voltaram);
      fichasDoElenco.current = g.fichas ?? {};
      referenciaDaHistoria.current = g.referencia;
      if (g.estilo) setEstilo(g.estilo);
      if (g.emAndamento) {
        const prontos = voltaram.filter((q) => q === "pronto").length;
        const faltam = voltaram.length - prontos;
        setInterrompida(faltam > 0);
        setAvisos([
          {
            tipo: "informacao" as const,
            texto:
              faltam > 0
                ? `A página recarregou no meio dos desenhos. ${prontos === 1 ? "O quadro que já estava pronto ficou guardado" : `Os ${prontos} quadros que já estavam prontos ficaram guardados`}; ${faltam === 1 ? "falta 1" : `faltam ${faltam}`}, e dá para continuar daqui.`
                : "A página recarregou, mas todos os quadros já estavam prontos e ficaram guardados.",
          },
        ]);
      }
    };
    if (!rascunho) {
      comDesenhos.then((h) => {
        if (h) devolver(h);
      });
      return;
    }
    setDocumento(rascunho.documento);
    setEstado("pronto");
    comDesenhos.then((h) => {
      if (h && (rascunho.semImagens || h.documento.id === rascunho.documento.id)) devolver(h);
    });
    if (rascunho.semImagens) {
      comDesenhos.then((d) => {
        if (d) return;
        setAvisoDoRascunho(
          "Sua história voltou com você, mas os desenhos não couberam na memória do navegador e " +
            "ficaram para trás. O texto e a estrutura dos quadros estão aqui."
        );
      });
    }
  }, []);

  // ── E ELE É GUARDADO AO SER DIGITADO (T9.4) ──────────────────────────────────────────────
  //
  // Não no clique de salvar: o achado tem DOIS caminhos até o login, e um deles é o link "Entrar"
  // do alto da página, que não passa por botão nenhum desta tela. A promessa da tela fala de ir
  // "até o login", não de ir por um botão — então o que atravessa qualquer saída é texto que se
  // guarda a cada tecla. São algumas centenas de bytes, em gaveta própria, e por isso ele nunca
  // disputa cota com as imagens do documento.
  useEffect(() => {
    guardarOTexto({ titulo, texto, formato, categoria, personagem, estilo });
  }, [titulo, texto, formato, categoria, personagem, estilo]);

  // ── T11.1 · A HISTORIA GERADA NAO SE PERDE AO SAIR DA TELA ──────────────────────────────
  //
  // O relato de 28/09: *"uma historia gerada se perdeu ao sair da tela sem salvar, sem nenhum
  // aviso"*. O documento ia para a gaveta SO no clique de salvar — e quem sai antes de clicar
  // perde os tres minutos de espera olhando os quadros serem desenhados um a um.
  //
  // E A RECOMENDACAO PEDIA DUAS COISAS: guardar sozinho e AVISAR antes de sair. A segunda nao
  // foi feita, e o motivo esta medido nesta bancada: o produto tinha dois dialogos nativos do
  // navegador e os dois sairam no T8.15/T8.31, com UMA excecao escrita — o editor, porque a
  // decisao de bloquear navegacao tem de ser sincrona e o React Router desta versao nao oferece
  // isso. Um `window.confirm` aqui seria o segundo, e o teste `nenhum-dialogo-do-navegador`
  // cobra exatamente um.
  //
  // ENTAO O CONSERTO E MELHOR QUE O AVISO: em vez de perguntar se pode perder, **sair deixa de
  // perder**. A guarda guarda e libera; voltar a esta tela devolve a historia. O que a tela
  // avisa, enquanto a historia nao esta na conta, e que ela ainda nao esta na conta — e isso e
  // texto na tela, nao caixa do navegador.
  // T26.R10 · a história pronta, com os desenhos, vai também ao banco do navegador (só desta aba,
  // vence em 2 h): a gaveta da aba não cabe 8 desenhos, e era ali que eles se perdiam.
  // T29.2 · e também DURANTE os desenhos, quadro a quadro: recarregar no meio deixa de perder os
  // prontos e de devolver a história anterior no lugar desta.
  useEffect(() => {
    if (!documento || salvo || lendoOBanco.current) return;
    if (estado !== "pronto" && estado !== "desenhando") return;
    const geracao: GeracaoGuardada = {
      pedidos,
      semente,
      quadros,
      fichas: fichasDoElenco.current,
      referencia: referenciaDaHistoria.current,
      estilo,
      emAndamento: estado === "desenhando",
    };
    void guardarOsDesenhos(documento, geracao);
  }, [documento, salvo, estado, pedidos, semente, quadros, estilo]);

  useEffect(() => {
    if (!documento || salvo) return;
    const soltar = registrarGuardaDeSaida(() => {
      guardarRascunho(documento);
      return true;
    });
    // E a saida que NAO passa por link do menu — voltar do navegador, trocar de rota por codigo.
    return () => {
      soltar();
      guardarRascunho(documento);
    };
  }, [documento, salvo]);

  // Uma desistência que atravessa renders. Sem isto, sair da tela no meio da geração deixaria o
  // laço rodando e escrevendo estado num componente que não existe mais.
  const desistir = useRef(false);
  /**
   * T21 · O PRIMEIRO QUADRO QUE SAI É A REFERÊNCIA DOS OUTROS, já reduzido a 512 px.
   *
   * Na letra (0049 T1.3): *"os mesmos elementos personagens que aparecem em um quadro serem eles
   * (...) em outro ângulo, de outro jeito, no quadro seguinte."* Guardado em `ref` e não em estado
   * porque nada na tela depende dele, e o laço de desenho precisa do valor na hora.
   */
  const referenciaDaHistoria = useRef<{ indice: number; img: string } | null>(null);
  // T26.R4 · as fichas do elenco: um desenho de cada personagem, sozinho e sem fundo.
  const fichasDoElenco = useRef<Record<string, string>>({});
  useEffect(() => () => { desistir.current = true; }, []);

  // T29.3 · com uma página em tela cheia, o fundo não rola e o Esc fecha.
  useEffect(() => {
    if (paginaAberta === null) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const tecla = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPaginaAberta(null);
    };
    window.addEventListener("keydown", tecla);
    return () => {
      document.body.style.overflow = antes;
      window.removeEventListener("keydown", tecla);
    };
  }, [paginaAberta]);

  useEffect(() => {
    document.title = "Do texto ao quadrinho · HQ-Lab";
    return () => { document.title = "HQ-Lab"; };
  }, []);

  /**
   * O BOTAO DE MONTAR: a guarda primeiro, o trabalho depois.
   *
   * A GUARDA LOCAL VEM ANTES DA REDE, e isso e de proposito duas vezes: ela nao gasta requisicao
   * nenhuma, e ela responde sobre o TEXTO QUE ENTROU. A guarda que existia perguntava sobre o que
   * VOLTOU ("saiu alguma fala?") e nao pode mais disparar, porque a quebra por regra transforma
   * qualquer linha numa narracao.
   */
  async function montar(event: React.FormEvent) {
    event.preventDefault();
    if (estado === "montando" || estado === "desenhando") return;

    const veredicto = pareceHistoria(texto);
    if (!veredicto.parece) {
      setErro(null);
      setRecusado(false);
      setLetrasSoltas(veredicto);
      return;
    }
    await montarAgora();
  }

  /**
   * O TRABALHO, SEM A GUARDA — e existir como funcao separada e o conserto de um defeito medido.
   *
   * A primeira versao fazia o botao "Tentar mesmo assim" guardar o texto num estado e reenviar o
   * formulario. Medido na vitrine em 29/09: a pergunta FICAVA na tela e nenhum pedido saia. A causa
   * e que o reenvio acontece no mesmo ciclo do clique, antes de o React reescrever o componente —
   * entao o `montar` reexecutava lendo o estado ANTIGO, nao achava o texto insistido, e perguntava
   * de novo. A porta ficava fechada com cara de aberta, que e o pior jeito de estar errado.
   *
   * Chamar o trabalho direto resolve sem depender de ordem de renderizacao, e apaga um estado: nao
   * ha o que lembrar, porque quem insiste esta clicando agora.
   */
  async function montarAgora(aprovado?: { texto: string; titulo?: string }) {
    if (estado === "montando" || estado === "desenhando") return;

    // O ASSISTENTE (0045) entrega o texto que a crianca APROVOU direto aqui, pelo mesmo motivo do
    // cabecalho acima: ler o estado logo depois de `setTexto` leria o texto ANTIGO.
    const textoUsado = aprovado?.texto ?? texto;
    const tituloUsado = aprovado?.titulo ?? titulo;

    setErro(null);
    setRecusado(false);
    setLetrasSoltas(null);
    setDocumento(null);
    setPlanoPendente(null);
    setAvisos([]);
    setInterrompida(false);
    setEstado("montando");
    desistir.current = false;
    // T29.2 · uma história nova começou: a anterior sai do navegador, para não voltar no lugar dela.
    void esquecerOsDesenhos();
    descartarRascunho();

    let resposta;
    try {
      resposta = await roteirizar({
        texto: textoUsado,
        titulo: tituloUsado.trim() || undefined,
        personagem: personagem.trim() || undefined,
        estilo: estiloParaEnviar(estilo),
        formato,
        categoria,
        autor: {
          id: globalState?.appUser?.Id(),
          nome: globalState?.appUser?.Name(),
          turma: globalState?.appUser?.EducationalInstitution(),
        },
      });
    } catch (e) {
      // A recusa da barreira tem caminho próprio: dizer "erro, tente de novo" para conteúdo
      // recusado faz a criança tentar o mesmo texto três vezes antes de desistir sem entender.
      if (e instanceof ConteudoRecusadoError) setRecusado(true);
      // T1.26 · A MENSAGEM PASSA PELA FRONTEIRA, como os avisos ja passavam. Aqui era
      // `e.message` cru: medido na vitrine em 26/09, a crianca lia "Failed to fetch" -- ingles,
      // jargao de navegador, numa tela de plataforma escolar. A frase em portugues existia e era
      // usada so quando o erro NAO era um `Error`, que e o caso que quase nunca acontece.
      setErro(falhaParaCrianca(e));
      setEstado("parado");
      return;
    }

    // ── T19 · PLANEJAR ANTES DE DESENHAR ─────────────────────────────────────────────────
    // "a partir disso vai gerar": a história vira primeiro o planejamento (tiras, quadros,
    // enquadramento, recordatório e balões), a pessoa vê, e só o clique dela monta o canvas.
    if (!entendeuAHistoria(resposta.documento)) {
      setErro(NAO_ENTENDI);
      setEstado("parado");
      return;
    }
    if (resposta.plano) {
      setPlanoPendente(resposta);
      setEstado("planejado");
      return;
    }
    await desenharOPlano(resposta);
  }

  /** T19 · do planejamento aprovado para o canvas e os desenhos. */
  async function desenharOPlano(resposta: RespostaDeRoteiro) {
    setPlanoPendente(null);
    desistir.current = false;
    let atual = resposta.documento;

    // ── O ROTEIRIZADOR ENTENDEU? (T4.2) ───────────────────────────────────────────────────
    //
    // Medido em 13/09 contra produção: com uma sequência de letras aleatórias — a entrada que ele
    // usou no teste — a rota devolve 200 com três quadros e NENHUM balão. A tela dizia `pronto` e
    // mostrava três retângulos vazios como se fossem o quadrinho dela.
    //
    // "não deu certo isso aí a parte do escreva história toda não funcionou". Ele desqualificou o
    // próprio teste na mesma frase, e mesmo assim tinha razão: o produto não avisou nada.
    //
    // A régua NÃO é julgar a escrita — ortografia ruim e frase torta são história, e recusá-las
    // seria pior que o defeito. A régua é sobre o PRODUTO: saiu alguma fala ou narração? Se não
    // saiu, o que ele devolveu é moldura vazia, e isso se diz.
    if (!entendeuAHistoria(atual)) {
      setErro(NAO_ENTENDI);
      setEstado("parado");
      return;
    }

    setDocumento(atual);
    // T1.26 · O QUE VEM DO BACKEND NÃO VAI CRU PARA A TELA.
    // Medido em produção em 26/09: com o crédito do provedor esgotado, a tela mostrava à criança
    // "HTTP 402 {"error":"You have depleted your monthly included credits. Purchase pre-paid
    // credits... subscribe to PRO"}". Erro cru, nome de fornecedor e propaganda de assinatura.
    // A fronteira é `avisos-para-crianca.ts`: o que não tem tradução não é renderizado.
    setAvisos(avisosParaCrianca(semOsAvisosDoCorte(resposta.avisos ?? [])));

    const prompts = resposta.promptsPorQuadro ?? [];
    // T11.3 · os pedidos ficam guardados para o refazer de um quadro so poder alcanca-los.
    setPedidos(prompts);
    setProgresso({ feitos: 0, total: prompts.length });
    setQuadros(prompts.map(() => "esperando" as EstadoDoQuadro));
    setInterrompida(false);
    setDuracoes([]);
    // T29.8 · o relógio do quadro só começa no quadro: as fichas do elenco são uma fase própria.
    setComecouEm(null);
    setEstado(prompts.length ? "desenhando" : "pronto");

    // ── UMA SEMENTE PARA A HISTÓRIA INTEIRA (T5.4) ────────────────────────────────────────
    //
    // *"Nada tem consistência (...) depois ele foi outra criança aleatória que não tem nada a
    // ver com o primeiro."* Sem semente, cada quadro é um sorteio independente — mesmo prompt
    // de estética, mesmo modelo, personagem diferente.
    //
    // Uma por GERAÇÃO e não uma fixa: uma fixa faria toda história do produto sair parecida com
    // todas as outras. O que se quer é que os quadros de ESTA história combinem entre si.
    const sementeDaHistoria = Math.floor(Math.random() * 4294967295);
    setSemente(sementeDaHistoria);

    // De quais provedores os quadros vieram. Se a cascata trocar no meio, os quadros não têm
    // como combinar — e a criança merece saber disso em vez de achar que desenhou mal.
    const provedores = new Set<string>();
    referenciaDaHistoria.current = null;
    // T22 · os quadros que saíram pelo caminho antigo porque TODAS as contas do klein se esgotaram.
    const peloCaminhoAntigo: number[] = [];

    // ── T26.R4 · O ELENCO ANTES DOS QUADROS ─────────────────────────────────────────────────
    //
    // O relatório de 09/10: «Lia virou um bombeiro homem (...) Pipoca virou papagaio, raposa,
    // coelho». A história tinha três personagens e UMA referência — o quadro 1, com o Tadeu.
    // Agora cada personagem que aparece em algum quadro ganha uma ficha: desenhado sozinho, de
    // corpo inteiro, com a aparência que a história deu, e recortado do fundo. Cada quadro vai ao
    // desenhista com as fichas de quem está nele. Custa um desenho por personagem (até 4).
    fichasDoElenco.current = {};
    const elenco = (resposta.elenco ?? []).filter((pers) => prompts.some((p) => p.quem?.includes(pers.nome)));
    if (elenco.length) {
      setAvisos((a) => [
        ...a,
        {
          tipo: "informacao" as const,
          texto: `Primeiro desenho cada personagem sozinho (${elenco.map((p) => p.nome).join(", ")}), para eles não mudarem de um quadro para o outro.`,
        },
      ]);
      for (let n = 0; n < elenco.length; n++) {
        const pers = elenco[n];
        if (desistir.current) return;
        setFase(
          elenco.length > 1
            ? `desenhando primeiro cada personagem sozinho: ${pers.nome} (${n + 1} de ${elenco.length})`
            : `desenhando primeiro ${pers.nome} sozinho`
        );
        try {
          const ficha = await Imgen.generateComReferencia(
            // T26.R4 · «em pé» fez do gato Pipoca um mascote de pé e vestido em 3 de 4 quadros (13/10).
            // Bicho é bicho — e a frase NÃO pode dizer «sem roupa»: a barreira de texto recusa essas
            // palavras (é o termo de nudez), e as 3 fichas foram recusadas na medição seguinte.
            `${pers.aparencia}. Corpo inteiro, de frente, sozinho. Se for um animal, é um bicho de estimação de verdade, nas quatro patas.`,
            "",
            sementeDaHistoria,
            "texto",
            "characters",
            undefined,
            estiloParaEnviar(estilo)
          );
          const reduzida = await reduzirParaReferencia(ficha.images[0], 1024);
          const sozinho = await Imgen.generateComReferencia("", reduzida, undefined, "isolar-personagem")
            .then((r) => reduzirParaReferencia(r.images[0], 1024, true))
            .catch(() => reduzirParaReferencia(ficha.images[0], 1024, true));
          fichasDoElenco.current[pers.nome] = sozinho;
        } catch (erro) {
          // Sem a ficha, o personagem segue como antes (pela referência da história). Klein
          // esgotado: não adianta tentar as outras fichas.
          console.warn(`[roteirizar] a ficha de ${pers.nome} não saiu`, erro);
          // Calado, isto custou uma medição inteira (13/10): as fichas recusadas e a história
          // voltando à referência única sem ninguém saber. A tela diz.
          setAvisos((a) => [
            ...a,
            {
              tipo: "ajuste" as const,
              texto: `Não deu para desenhar ${pers.nome} sozinho antes dos quadros, então ${pers.nome} pode mudar de um quadro para o outro.`,
            },
          ]);
          if (erro instanceof KleinEsgotadoError) break;
        }
      }
    }
    const comElenco = Object.keys(fichasDoElenco.current).length > 0;
    setFase(null);

    for (let i = 0; i < prompts.length; i++) {
      if (desistir.current) return;
      const p = prompts[i];
      // O quadro atual passa a "desenhando" ANTES da chamada, e o relogio reinicia com ele: a
      // estimativa do quadro conta do momento em que ele comecou, e nao do inicio de tudo.
      setQuadros((q) => q.map((e, j) => (j === i ? "desenhando" : e)));
      const comecou = Date.now();
      setComecouEm(comecou);
      // T21 · com referência, o quadro vai ao klein, que mantém o personagem; sem ela (é o
      // primeiro, ou o primeiro falhou), vai pelo caminho de sempre e passa a SER a referência.
      const ref = referenciaDaHistoria.current as { indice: number; img: string } | null;
      try {
        // T21 · O QUADRO 1 TAMBÉM VAI AO KLEIN, só com texto e com a descrição INTEIRA de "Quem é
        // o personagem" (pedido do 0049, TS-T1.3.6). Pelo caminho de sempre o orçamento de letras
        // cortava a descrição pela metade: 0 de 4 traços no quadro 1, copiados para os outros.
        // Pelo klein: 4 de 4 em 2 de 2 (12/10). Klein fora do ar → o caminho de sempre, para a
        // história não começar vazia; recusa de conteúdo não cai para lugar nenhum.
        //
        // T22 · O CAMINHO ANTIGO SÓ ENTRA COM O KLEIN ESGOTADO, e vale para todo quadro (0049 T3.2):
        // *"isso se terminar todas as chaves flux"*. O servidor só responde 503 depois de tentar
        // TODAS as contas; com uma viva, este ramo não acontece. Recusa de conteúdo e falha de rede
        // seguem como eram — não caem para lugar nenhum.
        // T30.2 · o mesmo caminho do «Redesenhar» (`pedirOQuadro`), para os dois não divergirem.
        const pedido = await pedirOQuadro(p, sementeDaHistoria, ref);
        if (pedido.peloCaminhoAntigo) peloCaminhoAntigo.push(i + 1);
        const { images, provider } = pedido;
        // O aviso de "motor diferente" conta só os quadros SEM referência: os que vêm do klein
        // seguem o quadro de referência por desenho, e não por sorteio de motor.
        if (!ref) provedores.add(provider);
        if (desistir.current) return;

        // O PLACEHOLDER NÃO É UM DESENHO, E NÃO PODE ENTRAR COMO SE FOSSE.
        //
        // Quando todos os provedores falham, a cascata do backend devolve um SVG cinza com o
        // texto do pedido dentro — é o último recurso, e existe para o editor não quebrar.
        // Medido em produção hoje: com a fila do Stable Horde cheia, ele aparece; oito rodadas
        // seguidas depois, nenhuma vez. É intermitente, não é constante.
        //
        // Se ele fosse colocado na página como imagem, a criança receberia um retângulo cinza
        // com aparência de desenho e nenhuma explicação — e concluiria que o HQ-Lab desenha mal,
        // e não que aquele quadro não ficou pronto. Tratar como não-desenhado é mais honesto: o
        // quadro fica vazio, o aviso aparece, e ela sabe que pode tentar de novo.
        if (provider === "placeholder") {
          setQuadros((q) => q.map((e, j) => (j === i ? "falhou" : e)));
          // Avisos que a PRÓPRIA TELA escreve já nascem na língua da criança — e passam a ter
          // tipo, como os que vêm do backend, para a tela poder distinguir informação de ajuste.
          setAvisos((a) => [
            ...a,
            {
              tipo: "ajuste" as const,
              quadro: i,
              texto: `O desenho do quadro ${i + 1} não ficou pronto — o serviço de imagem estava ocupado. Tente de novo daqui a pouco, ou desenhe esse quadro no editor.`,
            },
          ]);
        } else {
          atual = comImagemNoQuadro(atual, p.paginaId, p.quadroId, images[0], descricaoDoQuadro(p));
          setDocumento(atual);
          setQuadros((q) => q.map((e, j) => (j === i ? "pronto" : e)));
          setDuracoes((d) => [...d, Date.now() - comecou]);
          if (!ref && !comElenco) {
            // A REFERÊNCIA É O PERSONAGEM ISOLADO, e não o quadro inteiro. Medido no ar em 12/10:
            // com o quadro inteiro, o portão do quadro 1 vazou para o fundo do quadro 2 e a mesa
            // pedida sumiu; com a personagem isolada no branco, 0 vazamentos e a mochila em 3 de 3.
            // Isolar custa uma chamada a mais (~5 s), uma vez por história. Se falhar, o quadro
            // inteiro serve — pior, mas melhor que nenhuma referência.
            const reduzida: string | null = await reduzirParaReferencia(images[0]).catch(() => null);
            if (reduzida) {
              const isolada: string | null = await Imgen.generateComReferencia("", reduzida, sementeDaHistoria, "isolar-personagem")
                .then((r) => reduzirParaReferencia(r.images[0]))
                .catch(() => null);
              referenciaDaHistoria.current = { indice: i, img: isolada ?? reduzida };
            }
          }
        }
      } catch (e) {
        // Um quadro que falhou não derruba os outros: a história continua, o quadro fica sem
        // desenho e a criança pode desenhar aquele à mão depois. Perder cinco quadros prontos
        // porque o sexto falhou seria o pior resultado possível.
        setQuadros((q) => q.map((e, j) => (j === i ? "falhou" : e)));
        console.warn(`[roteirizar] o quadro ${p.quadroId} não foi desenhado`, e);
        setAvisos((a) => [
          ...a,
          {
            tipo: "ajuste" as const,
            quadro: i,
            // T21 · com referência, a falha é do klein, e ele não tem reserva em outro motor de
            // propósito: um quadro com outra criança é pior do que um quadro que espera.
            // Recusa da verificação de segurança é outra coisa que "ocupado": tentar de novo
            // igual não adianta, mudar a frase sim (T21 — o filtro da Cloudflare erra para o lado
            // seguro em cenas de escola, medido em 12/10).
            texto: e instanceof DesenhoRecusadoError
              ? `O desenho do quadro ${i + 1} foi recusado pela verificação de segurança, mesmo desenhado duas vezes. Mude um pouco a frase desse quadro e desenhe só ele de novo.`
              : ref
              ? `O quadro ${i + 1} não saiu mantendo o mesmo personagem — o serviço de desenho estava ocupado. Dá para desenhar só ele de novo daqui a pouco.`
              : `O desenho do quadro ${i + 1} não ficou pronto. Você pode gerar esse quadro depois, no editor.`,
          },
        ]);
      }
      setProgresso({ feitos: i + 1, total: prompts.length });
    }

    // ── A CASCATA TROCOU DE PROVEDOR NO MEIO DA HISTÓRIA? ─────────────────────────────────
    //
    // MEDIDO EM 15/09, seis quadros de uma mesma história pedidos em sequência, como esta tela
    // faz:
    //
    //   quadro 1  huggingface   1377 KB
    //   quadro 2  huggingface   1014 KB
    //   quadro 3  POLLINATIONS    40 KB   <- outro modelo, outra resolução, outro traço
    //   quadro 4  huggingface    801 KB
    //
    // O terceiro saiu de um provedor diferente porque o primeiro estava ocupado. Nenhuma
    // semente e nenhum prompt de estética consertam isso: são modelos diferentes.
    //
    // É `T5.4` na letra — *"depois foi uma coisa extremamente abstrata, não sei nem explicar o
    // que é isso aqui"*. Não dá para impedir aqui dentro; dá para PARAR DE ESCONDER, que é o
    // que a fala dele cobra quando ele diz que não entende o que aconteceu.
    // T22 · O AVISO DE TROCA DE MODELO (0049 T3.2): o que aconteceu, por quê, e o que isso muda.
    if (peloCaminhoAntigo.length) {
      const quais = peloCaminhoAntigo.length === 1
        ? `O quadro ${peloCaminhoAntigo[0]} foi desenhado`
        : `Os quadros ${peloCaminhoAntigo.join(", ")} foram desenhados`;
      setAvisos((a) => [
        ...a,
        {
          tipo: "ajuste" as const,
          texto:
            `${quais} por outro desenhista, que lê só o começo de cada cena. O que mantém o mesmo personagem de um quadro para o ` +
            "outro já usou todo o limite de desenhos de hoje, em todas as contas do HQ-Lab — por " +
            "isso esses quadros podem não combinar com os outros. Amanhã o limite volta, e dá para " +
            "desenhar só esses quadros de novo.",
        },
      ]);
    }

    if (provedores.size > 1) {
      setAvisos((a) => [
        ...a,
        {
          tipo: "ajuste" as const,
          // T9.17 · A SEGUNDA FRASE PROMETIA O QUE NINGUÉM MEDIU. Era "gerar de novo daqui a
          // pouco costuma sair mais parecido", e o QA de 02/10 anotou isso como promessa: quem
          // gera de novo cai em qualquer motor que esteja livre naquele minuto, e pode sair
          // mais parecido ou menos. Dizer "costuma" sobre um sorteio é a mesma falha da
          // recuperação de senha que jurava ter enviado o e-mail.
          texto:
            "Alguns quadros foram desenhados por um motor diferente dos outros, porque o primeiro " +
            "estava ocupado — por isso eles não combinam entre si. Gerar de novo usa o motor que " +
            "estiver livre na hora, então pode sair mais parecido ou não.",
        },
      ]);
    }

    if (!desistir.current) {
      // O relogio para junto com o estado: `comecouEm = null` e o sinal de "nada esta sendo
      // desenhado agora", e e o que faz o `setInterval` do componente se desmontar.
      setComecouEm(null);
      setFase(null);
      setEstado("pronto");
    }
  }

  const trabalhando = estado === "montando" || estado === "desenhando";

  /**
   * SALVAR O QUADRINHO GERADO NO ACERVO DA PESSOA.
   *
   * ── O LOGIN ENTRA AQUI, E SÓ AQUI (T2.16) ────────────────────────────────────────────────
   *
   * T2.16 pede que "escrever e deixar montar" passe pelo login. A decisão anterior dele, que
   * está registrada no `App.tsx`, diz o contrário para a ROTA: escrever não precisa de conta.
   *
   * As duas se reconciliam neste ponto: a rota continua pública — a criança escreve e vê a
   * história virar quadrinho sem conta nenhuma —, e o login aparece no primeiro momento em que
   * ele é de fato necessário, que é guardar aquilo em algum lugar que seja dela.
   *
   * E o quadrinho ATRAVESSA a ida ao login. Sem isso, tentar salvar seria a maneira mais rápida
   * de perder o trabalho: ver `rascunho-guardado.ts`.
   */
  /**
   * T11.3 · DESENHAR UM QUADRO DE NOVO, sem mexer nos outros.
   *
   * A MESMA semente da historia, de proposito: e ela que faz o quadro refeito combinar com os que
   * ja estao na pagina. Sorteada de novo, o quadro voltaria com outro personagem — o defeito do
   * T5.4 num quadro so, e dentro de uma pagina onde os vizinhos estao certos, o que e pior.
   *
   * E O PLACEHOLDER CONTINUA NAO SENDO DESENHO: a mesma regra do laco de geracao vale aqui, senao
   * refazer um quadro falho poria nele um retangulo cinza com cara de desenho.
   */
  /**
   * T26.R9a · PARAR DE DESENHAR. O relatório de 09/10: «o serviço levou cerca de 9 minutos e meio
   * para 8 quadros, sem botão de cancelar». Para o laço onde está (o quadro que já foi pedido pode
   * chegar e é descartado), deixa o que ficou pronto e oferece desenhar o resto um por um.
   */
  function pararDeDesenhar() {
    desistir.current = true;
    setFase(null);
    setQuadros((q) => q.map((e) => (e === "esperando" || e === "desenhando" ? "falhou" : e)));
    setEstado("pronto");
    setAvisos((a) => [
      ...a,
      {
        tipo: "informacao" as const,
        texto: "Parei de desenhar. Os quadros prontos ficaram; os outros dá para desenhar um por um, aqui embaixo das páginas.",
      },
    ]);
  }

  async function baixarComo(tipo: "pdf" | "png") {
    if (!documento || !paginasNaTela.current || baixando) return;
    setBaixando(tipo);
    // T29.1 · o «Gerando…» aparece ANTES do trabalho começar, e cada página é contada.
    setPassoDoBaixar(tipo === "pdf" ? "Preparando o PDF…" : "Preparando as imagens…");
    await respirar();
    try {
      const telas = await paginasComoImagens(paginasNaTela.current, documento.paginas.map((p) => p.largura), (n, total) =>
        setPassoDoBaixar(total > 1 ? `Montando a página ${n} de ${total}…` : "Montando a página…")
      );
      setPassoDoBaixar(tipo === "pdf" ? "Juntando as páginas no PDF…" : "Salvando as imagens…");
      await respirar();
      if (tipo === "pdf") await baixarPDF(titulo || documento.titulo || "meu quadrinho", telas);
      else await baixarPNGs(titulo || documento.titulo || "meu quadrinho", telas);
      setPassoDoBaixar(tipo === "pdf" ? "Pronto: o PDF foi para a pasta de downloads." : "Pronto: as imagens foram para a pasta de downloads.");
    } catch (e) {
      console.error("[roteirizar] baixar", e);
      setPassoDoBaixar(null);
      setAvisos((a) => [...a, { tipo: "ajuste" as const, texto: "Não deu para gerar o arquivo agora. Tente de novo." }]);
    } finally {
      setBaixando(null);
    }
  }

  /** T29.2 · depois de um recarregar no meio: desenha, um por um, os quadros que não ficaram prontos. */
  async function desenharOsQueFaltam() {
    setInterrompida(false);
    for (let i = 0; i < quadros.length; i++) {
      if (quadros[i] !== "pronto") await refazerOQuadro(i);
    }
  }

  /**
   * O pedido de UM quadro ao desenhista: com as fichas de quem está nele, ou com a referência da
   * história, ou só com o texto — sempre no estilo escolhido. Klein esgotado cai no caminho antigo
   * e diz que caiu; qualquer outro erro sobe.
   */
  async function pedirOQuadro(
    p: { prompt: string; cena?: string; quem?: string[] },
    s: number,
    ref: { indice: number; img: string } | null
  ): Promise<{ images: string[]; provider: string; peloCaminhoAntigo: boolean }> {
    const fichas = fichasDoElenco.current;
    const comElenco = Object.keys(fichas).length > 0;
    const doQuadro = (p.quem ?? []).filter((n) => fichas[n]).slice(0, 3).map((n) => ({ nome: n, imagem: fichas[n] }));
    const tag = estiloParaEnviar(estilo);
    const pedido = p.cena || p.prompt;
    try {
      const r = comElenco
        ? doQuadro.length
          ? await Imgen.generateComReferencia(pedido, doQuadro[0].imagem, s, "continuar", null, undefined, tag, doQuadro)
          : // Quadro sem ninguém do elenco (a rua vazia): só a cena, sem pôr personagem nela.
            await Imgen.generateComReferencia(pedido, "", s, "texto", null, undefined, tag)
        : ref
        ? await Imgen.generateComReferencia(pedido, ref.img, s, "continuar", null, undefined, tag)
        : await Imgen.generateComReferencia(pedido, "", s, "texto", null, personagem, tag);
      return { ...r, peloCaminhoAntigo: false };
    } catch (erro) {
      if (!(erro instanceof KleinEsgotadoError)) throw erro;
      return { ...(await Imgen.generateImages(p.prompt, undefined, s)), peloCaminhoAntigo: true };
    }
  }

  async function refazerOQuadro(i: number) {
    const p = pedidos[i];
    if (!documento || !p || semente === null || refazendo !== null) return;
    setRefazendo(i);
    setQuadros((q) => q.map((e, j) => (j === i ? "desenhando" : e)));
    try {
      // T21 · refazer também segue a referência da história, quando ela existe — inclusive o
      // próprio quadro de referência, que assim volta com o mesmo personagem que os vizinhos.
      const ref = referenciaDaHistoria.current as { indice: number; img: string } | null;
      // T30.2 · «"Redesenhar o quadro 3" ignorou o estilo Aquarela e a descrição do Tito: veio
      // Massinha 3D com outro menino» (relatório de 19/10). Com elenco e o quadro sem ninguém dele,
      // o refazer caía no desenhista antigo, sem estilo e com a descrição cortada. Agora é o mesmo
      // pedido da primeira vez.
      const pedido = await pedirOQuadro(p, semente, ref);
      // T22 · klein esgotado no refazer também cai no caminho antigo, e diz.
      const trocou = pedido.peloCaminhoAntigo;
      const { images, provider } = pedido;
      if (trocou) {
        setAvisos((a) => [
          ...a,
          {
            tipo: "ajuste" as const,
            quadro: i,
            texto: `O quadro ${i + 1} foi refeito por outro desenhista, porque o que mantém o mesmo personagem já usou todo o limite de hoje — ele pode não combinar com os outros.`,
          },
        ]);
      }
      if (provider === "placeholder" || !images?.length) {
        setQuadros((q) => q.map((e, j) => (j === i ? "falhou" : e)));
        setAvisos((a) => [
          ...a,
          {
            tipo: "ajuste" as const,
            quadro: i,
            texto: `O quadro ${i + 1} continua sem sair — o servico de imagem esta ocupado. Da para tentar de novo, ou desenhar esse quadro no editor depois de salvar.`,
          },
        ]);
        return;
      }
      setDocumento((d) =>
        d ? comImagemNoQuadro(d, p.paginaId, p.quadroId, images[0], descricaoDoQuadro(p)) : d
      );
      setQuadros((q) => q.map((e, j) => (j === i ? "pronto" : e)));
      // T26.R9 · o quadro ficou pronto: o aviso de que ele não tinha ficado sai da lista.
      setAvisos((a) => semOsAvisosDoQuadro(a, i));
    } catch (e) {
      console.warn(`[roteirizar] refazer o quadro ${p.quadroId} falhou`, e);
      setQuadros((q) => q.map((e, j) => (j === i ? "falhou" : e)));
      setAvisos((a) => [
        ...a,
        {
          tipo: "ajuste" as const,
          quadro: i,
          texto: `Não deu para desenhar o quadro ${i + 1} agora. Tente de novo daqui a pouco.`,
        },
      ]);
    } finally {
      setRefazendo(null);
    }
  }

  async function salvarNoAcervo() {
    if (!documento || salvando) return;

    const conta = globalState?.appUser;
    if (!conta) {
      const guardou = guardarRascunho(documento);
      navigate("/login", {
        state: {
          voltarPara: "/roteirizar",
          motivo: "sem-conta",
        },
      });
      if (!guardou.guardou) {
        console.warn("[roteirizar] não consegui guardar o rascunho para a ida ao login");
      }
      return;
    }

    setSalvando(true);
    setPassoDoSalvar(null);
    setErro(null);

    try {
      // ── AS IMAGENS PRIMEIRO, E CADA UMA UMA VEZ SÓ ───────────────────────────────────────
      //
      // O documento gerado traz cada desenho em base64 dentro do próprio elemento. O editor
      // referencia imagem por `imgId`, uma chave no catálogo privado da conta. Então cada
      // desenho vira um documento em `users/{uid}/imgs` e o elemento passa a apontar para ele.
      //
      // O mapa é por conteúdo: o mesmo desenho repetido em dois quadros sobe uma vez.
      const imagens = imagensDoDocumento(documento);
      const idPorImagem = new Map<string, string>();

      for (let i = 0; i < imagens.length; i++) {
        const refImagem = imagens[i];
        // O plural certo importa: "1 de 1 desenho" lê como defeito de programa.
        setPassoDoSalvar(
          imagens.length === 1
            ? "Guardando o desenho…"
            : `Guardando o desenho ${i + 1} de ${imagens.length}…`
        );
        // ── E CADA DESENHO PRECISA CABER NO DOCUMENTO (T5.6) ──────────────────────────────
        //
        // Um documento do Firestore cabe em 1 MiB, e o desenho vai INTEIRO no campo `url`, em
        // base64. Medido em 15/09 contra produção: "um gato gordo" dá 1,12 MiB e "uma cidade
        // cheia de prédios" dá 1,60 MiB. Quanto mais detalhe, maior — e é por isso que o
        // defeito parecia aleatório: teste bobo salva, história de verdade não.
        //
        // "E eu não consegui salvar o quadrinho" era isto, e a frase seguinte dele — "Não deu
        // pra salvar o quadrinho" — é a mensagem de erro logo abaixo, palavra por palavra.
        const encolhida = await encolherParaCaber(refImagem);
        if (encolhida.tentativaUsada) {
          console.info(
            `[roteirizar] desenho reencodado para caber: ${Math.round(encolhida.bytesAntes / 1024)}KB -> ` +
              `${Math.round(encolhida.bytesDepois / 1024)}KB`
          );
        }
        if (encolhida.aindaNaoCabe) {
          // NÃO manda tentar de novo, e a diferença é a mesma que a barreira de conteúdo já
          // tinha ensinado: falta de espaço é DETERMINÍSTICA. Repetir o mesmo desenho dá o mesmo
          // tamanho, e "tente de novo em instantes" faria a criança bater três vezes na mesma
          // porta antes de desistir sem entender.
          console.error("[roteirizar] desenho não coube nem na menor tentativa");
          setErro(
            "Um dos desenhos ficou grande demais para guardar, mesmo depois de eu tentar " +
              "reduzi-lo. O quadrinho continua aqui na tela. Uma cena com menos coisas dentro " +
              "costuma gerar um desenho menor — vale tentar reescrever essa parte da história."
          );
          setSalvando(false);
          setPassoDoSalvar(null);
          return;
        }
        const ref = await Firebase.pushDocument(`users/${conta.Id()}/imgs`, {
          type: "figures",
          url: encolhida.imagem,
          createdAt: Date.now(),
        });
        idPorImagem.set(refImagem, ref.id);
      }

      setPassoDoSalvar("Montando o quadrinho…");
      const { quadrinho, perdidos } = paraOEditor(documento, (ref) => idPorImagem.get(ref));

      const ref = await Firebase.pushDocument(`users/${conta.Id()}/comics`, quadrinho);

      // O que não teve correspondente no editor é DITO, e não some calado.
      const quantosPerdidos = Object.values(perdidos).reduce((t, n) => t + n, 0);
      if (quantosPerdidos > 0) {
        console.warn("[roteirizar] elementos sem correspondente no editor:", perdidos);
      }

      setPassoDoSalvar(null);
      setSalvo(ref.id);
      // T11.1 · A GAVETA ESVAZIA DEPOIS DE SALVAR, e nenhum dos dois cofres era limpo. Sem isto,
      // voltar a esta tela depois de salvar devolve a historia como se ela nunca tivesse sido
      // salva — e a crianca salva duas vezes, ou acha que o salvar nao funcionou.
      descartarRascunho();
      descartarOTexto();
      navigate(`/meus-quadrinhos/${ref.id}/editar`, {
        state: { veioDoRoteirizador: true },
      });
    } catch (e) {
      console.error("[roteirizar] falhou ao salvar", e);
      setErro(
        "Não deu para salvar o quadrinho agora. Ele continua aqui na tela — tente de novo em " +
          "instantes, e não feche esta aba."
      );
      setSalvando(false);
      setPassoDoSalvar(null);
    }
  }

  return (
    <div className="roteirizar-container">
      <Nav />

      <main className="roteirizar-conteudo">
        <header className="roteirizar-topo">
          <h1>Do texto ao quadrinho</h1>
          <p>
            Escreva a sua história do jeito que ela é na sua cabeça. O HQ-Lab separa as cenas,
            monta os quadros e desenha cada um.
          </p>
        </header>

        {/* ── T8.24 · O AVISO VEM ANTES, E A PORTA CONTINUA ABERTA ──────────────────────
            O achado, na letra: "/roteirizar abre para quem não está logado, com o menu de
            visitante, e DEIXA PREENCHER TUDO ANTES DE QUALQUER AVISO."

            Medido em produção em 24/09, deslogado: a rota abre, os dois campos aceitam texto,
            e não há aviso nenhum na tela. Confirmado.

            E O CONSERTO NÃO É FECHAR A ROTA, ainda que a sugestão do relatório (T8.24) diga
            "proteger /roteirizar como as outras rotas de criação". Fechá-la desfaz uma decisão
            deliberada e testada: o TS-T2.5.3 manteve a rota pública de propósito — "a rota
            continua pública, e o login aparece no primeiro momento em que ele é de fato
            necessário" — e construiu a travessia que leva a história inteira para dentro do
            login (`rascunho-guardado.ts`, em sessionStorage porque é computador de escola
            compartilhado). Fechar a porta jogaria fora as duas coisas.

            O que o achado descreve é ausência de AVISO, e é isso que se conserta: quem chega
            sem conta descobre agora, antes de escrever, que vai precisar de uma para guardar —
            e descobre junto que o que escrever não se perde no caminho. */}
        {!globalState?.appUser && (
          <p className="roteirizar-sem-conta" role="status">
            <strong>Você ainda não entrou.</strong> Pode escrever e ver o seu quadrinho ser
            montado agora mesmo — mas para <strong>guardar</strong> ele você vai precisar de uma
            conta. O que você escrever não se perde: ele vai junto com você até o login e volta
            do outro lado.
          </p>
        )}

        {/* O ASSISTENTE DE IA (0045). Ele so PROPOE; cada botao "Aprovar" chama o que esta tela
            ja faz — preencher os campos, montar o roteiro, abrir um rascunho privado. Publicar
            nao esta entre as propostas: e gesto da crianca, pelo botao dela. */}
        <PainelDoAssistente
          tela="roteirizar"
          rascunho={texto}
          logado={!!globalState?.appUser}
          aoAprovar={{
            formulario: (campos) => {
              if (campos.titulo) setTitulo(campos.titulo.slice(0, 80));
              if (campos.historia) setTexto(campos.historia);
            },
            roteirizar: (pedido) => {
              if (pedido.titulo) setTitulo(pedido.titulo.slice(0, 80));
              setTexto(pedido.texto);
              void montarAgora(pedido);
            },
            criarRascunho: (nome) =>
              navigate("/meus-quadrinhos/rascunho", {
                state: { formato, nome: nome.trim().slice(0, LETRAS_DO_NOME) },
              }),
          }}
        />

        <form className="roteirizar-form" onSubmit={montar}>
          <div className="campo">
            <label htmlFor="titulo">Título</label>
            <input
              id="titulo"
              value={titulo}
              maxLength={80}
              placeholder="O caderno perdido"
              onChange={(e) => setTitulo(e.target.value)}
              disabled={trabalhando}
            />
          </div>

          <div className="campo">
            <label htmlFor="texto">A sua história</label>
            <textarea
              id="texto"
              required
              rows={9}
              maxLength={20000}
              value={texto}
              placeholder={EXEMPLO}
              onChange={(e) => setTexto(e.target.value)}
              disabled={trabalhando}
              aria-describedby="texto-dica"
            />
            <span className="campo-dica" id="texto-dica">
              {/* T26.R3/R5 · a dica diz o que o site FAZ desde 13/10: linha = quadro, e o que vem
                  depois do travessão é a fala. Na tirinha, as linhas se encaixam em tiras de 3 (T19). */}
              Cada linha vira um quadro. Escreva a cena e, depois de um travessão, a fala: «Lia
              liga para os bombeiros. — Moço, o gato subiu na árvore!». A fala vira balão, a cena
              vira desenho, e linha sem fala vira narração.
              {formato === "tirinha" ? " Na tirinha, as linhas se encaixam em tiras de 3 quadros." : ""}
            </span>
            <button
              type="button"
              className="roteirizar-exemplo"
              disabled={trabalhando}
              onClick={() => setTexto(EXEMPLO)}
            >
              Usar o exemplo
            </button>

            {/* ── T11.4 · A CHECAGEM DO QUE ELA ESCREVEU AGORA ──────────────────────────────
                *"Quanto mais texto, mais confuso fica o resultado"* não era impressão: o campo
                aceita 20.000 caracteres e o pedido de desenho de cada cena usa os primeiros 90.
                Nada na tela dizia isso. O alerta AVISA e nunca recusa — a mesma regra que o
                `T4.2` fixou nesta tela para o aviso de "letras soltas". */}
            {leitura.alertas.length > 0 && (
              <div className="roteirizar-alerta-de-escrita" role="status">
                {leitura.alertas.map((a) => (
                  <p key={a.id}>{a.texto}</p>
                ))}
              </div>
            )}
          </div>

          {/* ── T12.7 · QUEM É O PERSONAGEM ─────────────────────────────────────
              O relatório de 28/09: *"não explica que é preciso descrever o personagem (idade,
              roupa, jeito), a emoção e o cenário"*, e três rodadas de teste apontam o mesmo
              sintoma — *"o contador vira uma criatura verde no segundo quadro"*.

              O campo é OPCIONAL e não é enfeite: o que a pessoa escrever aqui entra no pedido de
              desenho de TODO quadro, na frente do que a máquina inferiu da cena. É por isso que
              ele atravessa os quadros — semente igual com pedido diferente devolve desenho
              diferente, e o pedido de cada quadro é a cena dele. */}
          <div className="campo">
            <label htmlFor="personagem">Quem é o personagem? (dá para deixar em branco)</label>
            <input
              id="personagem"
              value={personagem}
              maxLength={200}
              placeholder="Ex: a Bia, 9 anos, tranças, camiseta amarela e mochila vermelha"
              onChange={(e) => setPersonagem(e.target.value)}
              disabled={trabalhando}
              aria-describedby="personagem-dica"
            />
            <span className="campo-dica" id="personagem-dica">
              Isto vai junto em <strong>todos</strong> os quadros. Descrever idade, roupa e cabelo
              é o que faz o mesmo personagem aparecer igual do começo ao fim.
            </span>
          </div>

          {/* ── T11.2 · COMO ESCREVER UMA BOA CENA ────────────────────────────────────────────
              *"As dicas que evitam os erros comuns estão só no Instagram."* São três, e elas estão
              nomeadas no próprio ponto: excesso de texto, falta de foco e ritmo linear demais. A
              primeira traz o número de verdade — 90 letras — em vez de pedir "texto curto", que é
              adjetivo. As três e os números moram em `como-escrever-a-cena.ts`, junto com a
              checagem acima, porque as duas falam das mesmas regras. */}
          <details className="roteirizar-dicas" open={!texto.trim()}>
            <summary>Como escrever uma boa cena</summary>
            <ul>
              {dicasCom(numerosDoDesenho).map((d) => (
                <li key={d.id}>
                  <strong>{d.titulo}</strong>
                  <span>{d.texto}</span>
                </li>
              ))}
            </ul>
          </details>

          <div className="roteirizar-escolhas">
            <div className="campo">
              <label htmlFor="formato">Formato da página</label>
              <select
                id="formato"
                value={formato}
                disabled={trabalhando}
                onChange={(e) => setFormato(e.target.value as FormatoPagina)}
              >
                {Object.entries(PADROES_PAGINA).map(([id, p]) => (
                  <option key={id} value={id}>{p.rotulo}</option>
                ))}
              </select>
              <span className="campo-dica">{PADROES_PAGINA[formato].explicacao}</span>
            </div>

            {/* T22 · O ESTILO DO DESENHO, por tag (0049 T3.1). Vale para a história inteira: o
                quadro 1 sai neste estilo e os outros o seguem pela referência. */}
            <div className="campo">
              <label htmlFor="estilo">Estilo do desenho</label>
              <select id="estilo" value={estilo} disabled={trabalhando} onChange={(e) => setEstilo(e.target.value)}>
                {ESTILOS_DA_TELA.map((e) => (
                  <option key={e.tag} value={e.tag}>{e.rotulo}</option>
                ))}
              </select>
            </div>

            <div className="campo">
              <label htmlFor="categoria">Categoria</label>
              <select
                id="categoria"
                value={categoria}
                disabled={trabalhando}
                onChange={(e) => setCategoria(e.target.value as Categoria)}
              >
                {(Object.keys(ROTULO_CATEGORIA) as Categoria[]).map((c) => (
                  <option key={c} value={c}>{ROTULO_CATEGORIA[c]}</option>
                ))}
              </select>
              <span className="campo-dica">
                É por aqui que a sua história é encontrada na biblioteca.
              </span>
            </div>
          </div>

          <button type="submit" className="roteirizar-botao" disabled={trabalhando || !texto.trim()}>
            {estado === "montando"
              ? "Planejando a história…"
              : estado === "desenhando"
              ? `Desenhando ${Math.min(terminados(quadros) + 1, progresso.total)} de ${progresso.total}…`
              : estado === "planejado"
              ? "Planejar de novo"
              : "Planejar a minha história"}
          </button>
        </form>

        {erro && (
          <p className={`roteirizar-erro ${recusado ? "recusa" : ""}`} role="alert">
            {erro}
            {recusado && (
              <>
                {" "}
                Tente contar a mesma cena com outras palavras — o HQ-Lab é usado por turmas
                inteiras, e por isso ele é rigoroso.
              </>
            )}
          </p>
        )}

        {/* ── A PERGUNTA, E NUNCA UMA RECUSA — `T4.2` ─────────────────────────────────────────
            Ele digitou `asdkjh askjdh ...` e recebeu um quadrinho como se aquilo fosse a historia
            dele. O conserto nao pode ser fechar a porta: uma crianca que escreve diferente do que
            esta regua espera perderia a propria historia. Entao a tela diz o que viu, mostra o
            exemplo, e deixa seguir com um clique. */}
        {letrasSoltas && (
          <div className="roteirizar-letras-soltas" role="alert">
            <p>
              <strong>Isso parece letras soltas, e não uma história.</strong> Das{" "}
              {letrasSoltas.palavras} palavras que você escreveu, {letrasSoltas.pronunciaveis} dá
              para ler
              {letrasSoltas.exemplos.length > 0 && (
                <> — eu não consegui ler {letrasSoltas.exemplos.map((p) => `"${p}"`).join(", ")}</>
              )}
              .
            </p>
            <p>
              Se você escreveu de propósito, pode seguir. Se foi sem querer, é melhor escrever a
              história antes — senão o quadrinho sai com as letras soltas dentro.
            </p>
            <div className="roteirizar-letras-soltas-acoes">
              <button
                type="button"
                className="roteirizar-botao"
                onClick={() => {
                  // Chama o trabalho DIRETO. Reenviar o formulario aqui leria o estado antigo e a
                  // pergunta voltaria — medido, e esta escrito no cabecalho do `montarAgora`.
                  setLetrasSoltas(null);
                  void montarAgora();
                }}
              >
                Tentar mesmo assim
              </button>
              <button
                type="button"
                className="roteirizar-botao-secundario"
                onClick={() => {
                  setLetrasSoltas(null);
                  const campo = document.getElementById("texto");
                  if (campo instanceof HTMLTextAreaElement) campo.focus();
                }}
              >
                Voltar e escrever
              </button>
            </div>
          </div>
        )}

        {avisoDoRascunho && (
          <p className="roteirizar-aviso-rascunho" role="status">
            {avisoDoRascunho}
          </p>
        )}

        {planoPendente?.plano && (
          <PlanoDoQuadrinho
            plano={planoPendente.plano}
            formato={formato}
            ocupado={trabalhando}
            aoDesenhar={() => void desenharOPlano(planoPendente)}
            aoMudar={() => {
              setPlanoPendente(null);
              setEstado("parado");
              const campo = document.getElementById("texto");
              if (campo instanceof HTMLTextAreaElement) campo.focus();
            }}
          />
        )}

        {documento && (
          <section className="roteirizar-resultado">
            <div className="roteirizar-resultado-topo">
              <h2>{documento.titulo}</h2>
              {/* ── O PROGRESSO, E ELE SUBSTITUIU UM CONTADOR QUE JA EXISTIA ──────────
                  Aqui havia `Desenhando os quadros — N de M prontos`, escrito em 09/09. Ele
                  respondia QUANTOS e nao respondia nenhuma das duas perguntas do T2.86: "ta
                  gerando de fato?" e "isto que estou vendo e o final?".

                  Com total = 1 — que era o caso dele na gravacao — aquele contador nascia parado
                  em "1 de 1" e nao se movia nunca. Ver `progresso.tsx` para a apuracao inteira. */}
              {estado === "desenhando" && (
                <>
                  <ProgressoDaGeracao
                    total={progresso.total}
                    quadros={quadros}
                    comecouEm={comecouEm}
                    mediaMs={mediaMedida(duracoes)}
                    fase={fase}
                  />
                  {/* T26.R9a · o botão que o testador procurou aos 9 minutos de espera. */}
                  <button type="button" className="roteirizar-parar" onClick={pararDeDesenhar}>
                    Parar de desenhar
                  </button>
                </>
              )}
              {estado === "pronto" && (
                <p className="roteirizar-progresso" aria-live="polite">
                  Pronto. {documento.paginas.length}{" "}
                  {documento.paginas.length === 1 ? "página" : "páginas"}.
                </p>
              )}

              {/* ── T12.1 · O SALVAR TAMBÉM NO TOPO ────────────────────────────────
                  A sugestão do relatório é literal: *"mostrar o botão de salvar também no topo do
                  resultado"*. O de baixo fica depois das páginas inteiras — na história dele, 5
                  quadros em 2 páginas —, e ele saiu sem nunca ter visto um botão de salvar.

                  É o MESMO botão: o mesmo `onClick`, o mesmo `disabled`. Dois controles com o
                  mesmo nome para a mesma ação é o que uma página longa pede; dois nomes
                  diferentes seria o defeito do T2.32 de volta, que eram cinco rótulos para dois
                  destinos. */}
              {estado === "pronto" && !salvo && (
                <button
                  type="button"
                  className="roteirizar-botao-salvar roteirizar-salvar-no-topo"
                  onClick={salvarNoAcervo}
                  disabled={salvando}
                >
                  {salvando ? "Salvando…" : "Salvar em Meus quadrinhos"}
                </button>
              )}
            </div>

            {avisos.length > 0 && (
              <ul className="roteirizar-avisos">
                {avisos.map((a, i) => (
                  <li key={i} className={`aviso-${a.tipo}`}>
                    {a.texto}
                  </li>
                ))}
              </ul>
            )}

            {/* T2.85 · O BOTÃO SÓ APARECE QUANDO ELE TEM O QUE FAZER.
                Numa janela onde `larguraAmpliada` é igual a 860, "Ver maior" não aumentaria nada —
                e um controle que não muda a tela ensina a desconfiar dos outros. */}
            {/* T29.3 · «No celular não dá para ler a HQ pronta: sem "Ver maior", sem toque para
                ampliar» (relatório de 14/10). O «Ver maior» sumia em janela estreita, e a página
                não respondia ao toque. Agora tocar numa página — em qualquer tela — abre ela
                sozinha, no tamanho real, onde dá para arrastar e ampliar com dois dedos. */}
            <div className="roteirizar-escala">
              {larguraAmpliada > 860 && (
                <button
                  type="button"
                  className="roteirizar-botao-escala"
                  onClick={() => setAmpliado((x) => !x)}
                  aria-pressed={ampliado}
                >
                  {ampliado ? "Ver menor" : "Ver maior"}
                </button>
              )}
              <button type="button" className="roteirizar-botao-escala" onClick={() => setPaginaAberta(0)}>
                Ver em tela cheia
              </button>
              <span className="roteirizar-escala-dica">
                {ampliado ? `Página a ${larguraAmpliada} px de largura. ` : ""}
                Toque numa página para abrir só ela, no tamanho real.
              </span>
            </div>

            <div
              ref={paginasNaTela}
              // T29.3 · «Ver maior» vazava pela direita (a folha a 1232 px dentro da coluna de
              // 960): ampliada, a faixa das páginas sai da coluna e ocupa a janela.
              className={`roteirizar-paginas ${ampliado ? "ampliadas" : ""}`}
              onClick={(e) => {
                const figura = (e.target as HTMLElement).closest(".motor-pagina");
                if (!figura || !figura.parentElement) return;
                setPaginaAberta(Array.from(figura.parentElement.children).indexOf(figura));
              }}
            >
              <VisualizadorDeDocumento
                documento={documento}
                larguraDisponivel={ampliado ? larguraAmpliada : 860}
              />
            </div>

            {paginaAberta !== null && documento.paginas[paginaAberta] && (
              <div
                className="roteirizar-tela-cheia"
                role="dialog"
                aria-modal="true"
                aria-label={`Página ${paginaAberta + 1} de ${documento.paginas.length}, em tela cheia`}
              >
                <div className="roteirizar-tela-cheia-barra">
                  <span>
                    Página {paginaAberta + 1} de {documento.paginas.length}
                  </span>
                  <div className="roteirizar-tela-cheia-botoes">
                    {documento.paginas.length > 1 && (
                      <>
                        <button type="button" disabled={paginaAberta === 0} onClick={() => setPaginaAberta((i) => Math.max(0, (i ?? 0) - 1))}>
                          Anterior
                        </button>
                        <button
                          type="button"
                          disabled={paginaAberta === documento.paginas.length - 1}
                          onClick={() => setPaginaAberta((i) => Math.min(documento.paginas.length - 1, (i ?? 0) + 1))}
                        >
                          Próxima
                        </button>
                      </>
                    )}
                    <button type="button" className="roteirizar-tela-cheia-fechar" onClick={() => setPaginaAberta(null)} autoFocus>
                      Fechar
                    </button>
                  </div>
                </div>
                <p className="roteirizar-tela-cheia-dica">Arraste para ver o resto da página. Com dois dedos, amplia ou reduz.</p>
                <div className="roteirizar-tela-cheia-folha">
                  <div style={{ width: documento.paginas[paginaAberta].largura }}>
                    <VisualizadorDePagina pagina={documento.paginas[paginaAberta]} larguraDisponivel={documento.paginas[paginaAberta].largura} />
                  </div>
                </div>
              </div>
            )}

            {/* ── T11.3 · O BOTAO ATRAS DA FRASE QUE A TELA JA DIZIA ──────────────────────
                Quando um quadro falha, o aviso manda *"tentar de novo"* — e nao havia controle
                nenhum atras disso: gerar a historia de novo sortearia tambem os quadros que
                ficaram bons. O bloco fica aqui, embaixo das paginas, porque e olhando a pagina
                que a crianca ve qual quadro ficou vazio. */}
            {/* T26.M3 · e qualquer quadro se redesenha, não só o que falhou: o relatório pedia
                «trocar ou redesenhar um quadro isolado na própria página de resultado». */}
            {estado === "pronto" && quadros.length > 0 && (
              <div className="roteirizar-refazer">
                <p>
                  {quadros.some((q) => q === "falhou")
                    ? `${quadros.filter((q) => q === "falhou").length === 1 ? "Um quadro não ficou pronto." : `${quadros.filter((q) => q === "falhou").length} quadros não ficaram prontos.`} O resto da história está aqui — dá para desenhar só o que faltou.`
                    : "Não gostou de um quadro? Dá para desenhar só ele de novo — os outros ficam."}
                </p>
                {/* T30.14 · «Depois de "Redesenhar o quadro 3", aos 3 e aos 21 segundos não vi sinal
                    nenhum na tela» (19/10): o botão mudava, mas longe de onde se olha. */}
                {refazendo !== null && (
                  <p className="roteirizar-refazendo" role="status">
                    Desenhando de novo o quadro {refazendo + 1}… leva perto de meio minuto. Os outros quadros ficam como estão.
                  </p>
                )}
                {/* T29.2 · um botão para todos os que faltam, depois de um recarregar no meio. */}
                {interrompida && quadros.some((q) => q !== "pronto") && (
                  <button
                    type="button"
                    className="roteirizar-botao roteirizar-continuar"
                    onClick={() => void desenharOsQueFaltam()}
                    disabled={refazendo !== null || semente === null}
                  >
                    {`Continuar: desenhar ${quadros.filter((q) => q !== "pronto").length === 1 ? "o quadro que falta" : `os ${quadros.filter((q) => q !== "pronto").length} quadros que faltam`}`}
                  </button>
                )}
                <div className="roteirizar-refazer-botoes">
                  {quadros.map((q, i) => (
                    <button
                      key={i}
                      type="button"
                      className={q === "falhou" ? "roteirizar-refazer-falhou" : undefined}
                      onClick={() => refazerOQuadro(i)}
                      disabled={refazendo !== null || semente === null || !pedidos[i]}
                    >
                      {refazendo === i
                        ? `Desenhando o quadro ${i + 1}…`
                        : q === "falhou"
                          ? `Desenhar o quadro ${i + 1}`
                          : `Redesenhar o quadro ${i + 1}`}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {estado === "pronto" && (
              <div className="roteirizar-baixar">
                <p>Leve o quadrinho com você — não precisa de conta:</p>
                <div className="roteirizar-baixar-botoes">
                  <button type="button" onClick={() => baixarComo("pdf")} disabled={baixando !== null}>
                    {baixando === "pdf" ? "Gerando o PDF…" : "Baixar em PDF"}
                  </button>
                  <button type="button" onClick={() => baixarComo("png")} disabled={baixando !== null}>
                    {baixando === "png" ? "Gerando as imagens…" : "Baixar as páginas (PNG)"}
                  </button>
                </div>
                {passoDoBaixar && (
                  <p className="roteirizar-passo-do-baixar" role="status" aria-live="polite">
                    {passoDoBaixar}
                  </p>
                )}
              </div>
            )}

            {/* ── SALVAR, E É O ÚNICO CAMINHO QUE PEDE CONTA ────────────────────────────────
                Só aparece quando a geração termina: oferecer "salvar" no meio dos desenhos
                convidaria a criança a guardar um quadrinho pela metade e a achar que o resto
                chegaria depois. */}
            {/* T11.1 · ENQUANTO A HISTORIA NAO ESTA NA CONTA, A TELA DIZ ISSO — e dizer na tela
                e o que substitui a caixa do navegador que o produto nao usa mais. Sair deixou de
                perder: a historia fica na gaveta da aba e volta quando a crianca volta. */}
            {estado === "pronto" && !salvo && (
              <p className="roteirizar-ainda-nao-salvo" role="status">
                Esta história ainda <strong>não está na sua conta</strong>. Ela fica guardada nesta
                aba se você sair e voltar, e se perde quando a aba fechar.
              </p>
            )}

            {estado === "pronto" && (
              <div className="roteirizar-guardar">
                <button
                  type="button"
                  className="roteirizar-botao-salvar"
                  onClick={salvarNoAcervo}
                  disabled={salvando || !!salvo}
                >
                  {salvando ? "Salvando…" : "Salvar em Meus quadrinhos"}
                </button>
                {/* ── A ESPERA PASSA A TER TAMANHO (T6.4) ──────────────────────────────────
                    "Tá salvando infinitamente" é o que uma espera sem passo parece. Dizer qual
                    desenho está subindo, de quantos, transforma "infinitamente" em "falta um".
                    `aria-live` porque quem usa leitor de tela é quem mais sofre com tela calada. */}
                {salvando && passoDoSalvar && (
                  <p className="roteirizar-passo-do-salvar" aria-live="polite">
                    {passoDoSalvar}
                  </p>
                )}
                {/* ── T11.5 · O QUE O BOTAO FAZ, DITO ANTES DO CLIQUE ──────────────────────
                    O ponto diz que a funcao automatica *"entrega a HQ pronta e pula justamente as
                    etapas em que o aluno aprende"*, e recomenda liga-la ao quadro a quadro: *"a
                    historia completa gera um rascunho que abre direto no quadro a quadro"*.
                    **Isso JA e o que acontece** desde 12/09 — salvar navega para o editor daquele
                    quadrinho —, e a tela nao dizia. "Voce pode continuar editando" descreve uma
                    possibilidade; o que acontece e que o editor ABRE, e e nele que a crianca troca
                    os desenhos e reescreve os baloes. */}
                <p className="roteirizar-guardar-nota">
                  {globalState?.appUser
                    ? "Salvar abre este quadrinho no editor quadro a quadro, com os desenhos e os balões já no lugar — é lá que você troca o que não ficou bom e reescreve as falas."
                    : "Para guardar este quadrinho você precisa entrar. Sua história vai junto e volta com você."}
                </p>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}

export default Roteirizar;
